import { useEffect, useState } from 'react';

import {
  getSystemThemePreference,
  resolveTheme,
} from 'ducks/layout/layoutSlice';

import { THEME_NAMES } from '@polyai/ui/styles/config/theme.constants';

import { useAppSelector } from 'hooks/reduxHooks';

export const useResolvedTheme = (): THEME_NAMES => {
  const selectedTheme = useAppSelector(state => state.layout.theme);
  const [resolvedTheme, setResolvedTheme] = useState<THEME_NAMES>(() =>
    resolveTheme(selectedTheme),
  );

  useEffect(() => {
    // Always update resolved theme when selection changes
    setResolvedTheme(resolveTheme(selectedTheme));

    // Only set up listener if SYSTEM is selected
    if (selectedTheme !== THEME_NAMES.SYSTEM) {
      return;
    }

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = () => {
      setResolvedTheme(getSystemThemePreference());
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    // Legacy browsers (Safari < 14)
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [selectedTheme]);

  // Toggle .dark class on document for Tailwind CSS dark mode
  useEffect(() => {
    if (resolvedTheme === THEME_NAMES.DARK) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [resolvedTheme]);

  return resolvedTheme;
};
